import React, { useState, useEffect } from 'react';
import {
  Home,
  Building,
  Phone,
  Shield,
  Users,
  Key,
  Search,
  Star,
  ArrowRight,
  Play,
  Handshake,
  ClipboardCheck,
  MessageCircle,
  Wrench,
  Zap,
  Hammer,
  PaintRoller,
  Bug,
  Plug,
} from 'lucide-react';
import MainHeader from '../../Components/Header/MainHeader';
import MainFooter from '../../Components/Footer/MainFooter';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.rekebisha.RekebishaClient&pcampaignid=web_share";
const FUNDI_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.whim.RekebishaFundi001&pcampaignid=web_share";

const heroImage = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=1920&auto=format&fit=crop";
const videoFallbackImage = "https://images.unsplash.com/photo-1523217582562-09d0def993a6?q=80&w=1920&auto=format&fit=crop";
const videoSrc = "https://assets.mixkit.co/videos/preview/mixkit-modern-house-with-a-swimming-pool-4694-large.mp4";

const propertyTypes = [
  {
    name: "Apartments",
    description: "Studio to four bedroom units in the neighbourhoods you want",
    image: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?q=80&w=800&auto=format&fit=crop",
  },
  {
    name: "Bungalows and Mansionettes",
    description: "Standalone homes with room to grow your family",
    image: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?q=80&w=800&auto=format&fit=crop",
  },
  {
    name: "Commercial Spaces",
    description: "Offices and retail spaces for your business",
    image: "https://images.unsplash.com/photo-1449844908441-8829872d2607?q=80&w=800&auto=format&fit=crop",
  },
  {
    name: "Luxury Homes",
    description: "Premium villas and estates for buyers who want more",
    image: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=800&auto=format&fit=crop",
  },
];

const fundiServices = [
  {
    icon: Wrench,
    name: "Plumber and Sanitary Fittings",
    description: "Leaks, blockages, tanks, showers and full sanitary installations",
  },
  {
    icon: Zap,
    name: "Electrician and Electrical Fittings",
    description: "Wiring, sockets, lighting and safe electrical installations",
  },
  {
    icon: Hammer,
    name: "Carpenter",
    description: "Doors, cabinets, wardrobes, shelving and furniture repairs",
  },
  {
    icon: PaintRoller,
    name: "Painter",
    description: "Interior and exterior painting, preparation and finishing",
  },
  {
    icon: Bug,
    name: "Fumigation Expert",
    description: "Pest control and fumigation for homes and business premises",
  },
  {
    icon: Plug,
    name: "Household Electronics Expert",
    description: "TVs, fridges, cookers and other household appliance repairs",
  },
];

const steps = [
  {
    icon: Search,
    title: "Search Homes Or Fundis",
    description: "Browse verified properties to let or buy, or filter rated fundis by trade, county and sub county.",
  },
  {
    icon: MessageCircle,
    title: "Connect Directly",
    description: "Message or call the homeowner, agent or fundi straight from their listing. No middlemen and no hidden fees.",
  },
  {
    icon: Key,
    title: "Move In Or Get It Fixed",
    description: "Agree on terms yourself, move into your new home or have the work done on your own timeline.",
  },
];

const testimonials = [
  {
    name: "Jane Wanjiku",
    location: "Nairobi",
    text: "I found my apartment on Rekebisha and spoke to the landlord directly the same day. No agent fees, no waiting around.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?q=80&w=150&h=150&auto=format&fit=crop&crop=face",
  },
  {
    name: "David Kimani",
    location: "Mombasa",
    text: "As a homeowner I listed my rental property and had a genuine tenant reach out within days. The app made it simple.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=150&h=150&auto=format&fit=crop&crop=face",
  },
  {
    name: "Mary Achieng",
    location: "Kisumu",
    text: "Buying our first home felt overwhelming until we found Rekebisha. We spoke to the seller ourselves and closed with confidence.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?q=80&w=150&h=150&auto=format&fit=crop&crop=face",
  },
  {
    name: "Peter Mwangi",
    location: "Nakuru",
    text: "My kitchen sink burst on a Sunday. I found a rated plumber near me on Rekebisha and he was at the house within the hour.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=150&h=150&auto=format&fit=crop&crop=face",
  },
];

export default function HomePage() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MainHeader />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-navy/70"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 relative">
          <div className="max-w-2xl">
            <span className="inline-block text-brandgreen font-semibold tracking-wide uppercase text-sm mb-4">
              Kenya's Marketplace For Homes And Fundis
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Find A Home. Find A Fundi. One App.
            </h1>
            <p className="text-lg text-gray-200 mb-10 max-w-xl">
              Rekebisha removes the middlemen. Browse verified houses to let or buy, or hire a rated fundi for the job at hand, and talk to the person on the other side yourself.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <a
                href="/find-rental"
                className="inline-flex items-center justify-center px-8 py-4 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
              >
                <Search className="w-5 h-5 mr-2" />
                Browse Properties
              </a>
              <a
                href="/find-fundi"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-navy transition-colors duration-300"
              >
                <Wrench className="w-5 h-5 mr-2" />
                Find A Fundi
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-200">
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-brandgreen mr-2" />
                Verified Listings And Fundis
              </div>
              <div className="flex items-center">
                <Handshake className="w-5 h-5 text-brandgreen mr-2" />
                Direct Contact, No Middlemen
              </div>
              <div className="flex items-center">
                <Users className="w-5 h-5 text-brandgreen mr-2" />
                Trusted Across All 47 Counties
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-navy mb-2">1,000+</div>
              <div className="text-gray-600">Properties Listed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-navy mb-2">2,000+</div>
              <div className="text-gray-600">Verified Fundis</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-navy mb-2">47</div>
              <div className="text-gray-600">Counties Covered</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-navy mb-2">10,000+</div>
              <div className="text-gray-600">Happy Clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Marketplaces Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-4">Two Marketplaces, One App</h2>
            <p className="text-xl text-gray-600">A place to live, and the people who keep it running</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="relative rounded-2xl overflow-hidden min-h-[420px]">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=900&auto=format&fit=crop)` }}
              ></div>
              <div className="absolute inset-0 bg-navy/70"></div>
              <div className="relative h-full flex flex-col justify-end p-10">
                <Home className="w-10 h-10 text-brandgreen mb-4" />
                <h3 className="text-3xl font-bold text-white mb-4">Houses To Let Or Buy</h3>
                <p className="text-gray-200 mb-6 max-w-md">
                  Search apartments, bungalows, mansionettes and commercial spaces across Kenya, then contact the owner or agent yourself.
                </p>
                <a
                  href="/find-rental"
                  className="inline-flex items-center w-fit px-6 py-3 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
                >
                  Browse Properties
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden min-h-[420px]">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=900&auto=format&fit=crop)` }}
              ></div>
              <div className="absolute inset-0 bg-navy/70"></div>
              <div className="relative h-full flex flex-col justify-end p-10">
                <Wrench className="w-10 h-10 text-brandgreen mb-4" />
                <h3 className="text-3xl font-bold text-white mb-4">Fundis And Handymen</h3>
                <p className="text-gray-200 mb-6 max-w-md">
                  Plumbers, electricians, carpenters, painters and more. Every fundi is verified and rated by the clients who hired them.
                </p>
                <a
                  href="/find-fundi"
                  className="inline-flex items-center w-fit px-6 py-3 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
                >
                  Find A Fundi
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">How Rekebisha Works</h2>
            <p className="text-xl text-gray-300">Three simple steps, whether you need a house or a hand</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <div className="w-16 h-16 bg-brandgreen rounded-full flex items-center justify-center mb-6">
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-300 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-4">Every Type Of Property</h2>
            <p className="text-xl text-gray-600">Whatever you are looking for, there is a listing waiting for you</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {propertyTypes.map((type, index) => (
              <a
                key={index}
                href="/find-rental"
                className="group relative rounded-2xl overflow-hidden h-72 block"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                  style={{ backgroundImage: `url(${type.image})` }}
                ></div>
                <div className="absolute inset-0 bg-navy/60 group-hover:bg-navy/70 transition-colors duration-300"></div>
                <div className="relative h-full flex flex-col justify-end p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{type.name}</h3>
                  <p className="text-gray-200 text-sm">{type.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Fundi Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-4">Every Trade You Need</h2>
            <p className="text-xl text-gray-600">Verified fundis, rated by the clients who hired them</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {fundiServices.map((service, index) => (
              <a
                key={index}
                href="/find-fundi"
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-brandgreen hover:shadow-lg transition-all duration-300 block"
              >
                <div className="w-14 h-14 bg-brandgreen/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-brandgreen transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-brandgreen group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3">{service.name}</h3>
                <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                <span className="inline-flex items-center text-brandgreen font-semibold">
                  Find one near you
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </a>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="/find-fundi"
              className="inline-flex items-center px-8 py-4 bg-navy text-white font-bold rounded-lg hover:bg-navy-dark transition-colors duration-300"
            >
              <Search className="w-5 h-5 mr-2" />
              Browse All Fundis
            </a>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section
        className="relative py-32 bg-cover bg-center"
        style={{ backgroundImage: `url(${videoFallbackImage})` }}
      >
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={videoSrc}
          poster={videoFallbackImage}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 bg-navy/70"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <ClipboardCheck className="w-12 h-12 text-brandgreen mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Know Who You Are Dealing With
          </h2>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">
            Every property comes with real photos and details from the owner or agent, and every fundi has a portfolio of past work and a rating from real clients. You know what to expect before you commit.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">What Our Users Say</h2>
            <p className="text-xl text-gray-300">Real stories from tenants, buyers, homeowners and fundis</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="flex items-center mb-6">
                <img
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].name}
                  className="w-16 h-16 rounded-full mr-4 object-cover"
                />
                <div>
                  <h3 className="text-xl font-bold text-white">{testimonials[currentTestimonial].name}</h3>
                  <p className="text-gray-300">{testimonials[currentTestimonial].location}</p>
                </div>
                <div className="ml-auto flex space-x-1">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-brandgreen fill-current" />
                  ))}
                </div>
              </div>
              <p className="text-lg text-gray-200 leading-relaxed">
                "{testimonials[currentTestimonial].text}"
              </p>
            </div>

            <div className="flex justify-center mt-8 space-x-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  aria-label={`Show testimonial ${index + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? 'bg-brandgreen w-8' : 'bg-white/30 w-2'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* List With Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-4">Have Something To Offer?</h2>
            <p className="text-xl text-gray-600">Rekebisha works for both sides of the marketplace</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-2xl p-10">
              <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mb-6">
                <Building className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-4">Agents And Homeowners</h3>
              <p className="text-gray-600 mb-6">
                List a property in minutes and reach genuine buyers and tenants directly through the app.
              </p>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-6 py-3 bg-navy text-white font-bold rounded-lg hover:bg-navy-dark transition-colors duration-300"
              >
                List A Property
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>

            <div className="bg-gray-50 rounded-2xl p-10">
              <div className="w-16 h-16 bg-brandgreen rounded-full flex items-center justify-center mb-6">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-4">Fundis And Handymen</h3>
              <p className="text-gray-600 mb-6">
                Build a portfolio, collect ratings and get hired by clients looking for your trade in your county.
              </p>
              <a
                href={FUNDI_PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-6 py-3 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
              >
                Join As A Fundi
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-6">Ready To Get Started?</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join the tenants, buyers, homeowners, agents and fundis who use Rekebisha to connect directly, with no middlemen involved.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-8 py-4 bg-brandgreen text-white font-bold rounded-lg hover:bg-brandgreen-dark transition-colors duration-300"
              >
                <Play className="w-5 h-5 mr-2" />
                Download The Rekebisha App
              </a>

              <a
                href="tel:0733212124"
                className="inline-flex items-center text-xl font-bold text-navy hover:text-brandgreen transition-colors duration-300"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Us: 0733 212 124
              </a>
            </div>
          </div>
        </div>
      </section>

      <MainFooter />
    </div>
  );
}
